'use client';

import { useEffect, useState, type FC } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { Alert } from 'react-bootstrap';
import { Clock } from 'lucide-react';
import { useOptionalMcpChatModel } from './McpChatModelContext';

/** How long an Auto reply may wait for its first text before we explain the delay. */
const AUTO_SLOW_HINT_MS = 30_000;

/**
 * Shown under the thinking indicator when Auto is selected and the reply has
 * not started after {@link AUTO_SLOW_HINT_MS}. Measured from the message's
 * creation so a remount (or a resumed run) does not restart the clock.
 */
export const McpAutoSlowHint: FC = () => {
  const isAuto = useOptionalMcpChatModel()?.isAuto ?? false;
  const createdAtMs = useAuiState(s => s.message.createdAt?.getTime());
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAuto) {
      setVisible(false);
      return;
    }
    const startedAt = createdAtMs ?? Date.now();
    const remaining = AUTO_SLOW_HINT_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      setVisible(true);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), remaining);
    return () => window.clearTimeout(timer);
  }, [isAuto, createdAtMs]);

  if (!visible) return null;

  return (
    <Alert
      variant="info"
      className="mcp-auto-slow-hint d-flex align-items-start gap-2 py-2 px-3 mb-0 fs--1"
      dir="rtl"
      role="status"
      aria-live="polite"
    >
      <Clock className="mcp-auto-slow-hint__icon" size={14} aria-hidden />
      <span>
        پاسخ کمی بیشتر از معمول طول کشیده است. در حالت Auto مدل به‌صورت خودکار
        انتخاب می‌شود و در ساعات شلوغ ممکن است کندتر پاسخ دهد. برای پاسخ
        سریع‌تر، می‌توانید از کنار دکمه ارسال یک مدل مشخص انتخاب کنید.
      </span>
    </Alert>
  );
};
